import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { LOAN_TYPES, CONTACT } from "../data/site";

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

const inputCls =
  "w-full bg-[#F4F4F5] border border-transparent rounded-xl px-4 py-3.5 text-[#09090B] placeholder:text-zinc-400 focus:bg-white focus:border-[#6C2BD9] focus:outline-none focus:ring-2 focus:ring-[#6C2BD9]/20 transition-colors";
const labelCls = "block text-sm font-medium text-zinc-700 mb-2";

export const LeadForm = ({ compact = false }) => {
  const [loanType, setLoanType] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");

    const form = e.target;
    const data = new FormData(form);
    data.append("access_key", WEB3FORMS_KEY || "");
    data.append("loan_type", loanType);
    data.append("subject", "New Loan Enquiry from CARDED Website");
    data.append("from_name", "CARDED Website");

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: data,
      });
      const json = await res.json();
      if (json.success) {
        setStatus("success");
        form.reset();
        setLoanType("");
      } else {
        setStatus("error");
        setErrorMsg(json.message || "Submission failed. Please try again.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg("Network error. Please try again or call us directly.");
    }
  };

  return (
    <div
      className="relative rounded-3xl border border-[#E4E4E7] bg-white p-6 sm:p-8 shadow-xl shadow-[#6C2BD9]/5"
      data-testid="lead-capture-form"
    >
      <div className="mb-6">
        <span className="text-xs font-semibold tracking-[0.25em] uppercase text-[#6C2BD9]">
          Get Started
        </span>
        <h3 className="mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-[#09090B]">
          Apply Now
        </h3>
        <p className="mt-1 text-sm text-zinc-500">
          Share your details and our representative will call you back.
        </p>
      </div>

      <AnimatePresence mode="wait">
        {status === "success" ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-10 text-center"
            data-testid="lead-success-message"
          >
            <CheckCircle2 className="h-14 w-14 text-[#6C2BD9]" />
            <p className="mt-4 text-lg font-medium text-[#09090B]">
              Thank you. Our representative will contact you shortly.
            </p>
            <button
              type="button"
              onClick={() => setStatus("idle")}
              className="mt-6 text-sm font-medium text-[#6C2BD9] hover:underline"
              data-testid="lead-reset-button"
            >
              Submit another enquiry
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={onSubmit}
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {/* Honeypot */}
            <input type="checkbox" name="botcheck" className="hidden" style={{ display: "none" }} tabIndex="-1" autoComplete="off" />

            <div>
              <label className={labelCls} htmlFor="lead-name">Full Name</label>
              <input
                id="lead-name"
                name="name"
                type="text"
                required
                placeholder="Enter your full name"
                className={inputCls}
                data-testid="lead-name-input"
              />
            </div>

            <div className={compact ? "" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
              <div>
                <label className={labelCls} htmlFor="lead-mobile">Mobile Number</label>
                <input
                  id="lead-mobile"
                  name="mobile"
                  type="tel"
                  required
                  pattern="[0-9+ ]{10,15}"
                  placeholder="10-digit mobile number"
                  className={inputCls}
                  data-testid="lead-mobile-input"
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="lead-city">City</label>
                <input
                  id="lead-city"
                  name="city"
                  type="text"
                  required
                  placeholder="Your city"
                  className={inputCls}
                  data-testid="lead-city-input"
                />
              </div>
            </div>

            <div className={compact ? "" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
              <div>
                <label className={labelCls}>Loan Type</label>
                <Select value={loanType} onValueChange={setLoanType} required>
                  <SelectTrigger
                    className="h-[52px] w-full rounded-xl bg-[#F4F4F5] border-transparent px-4 text-base focus:ring-2 focus:ring-[#6C2BD9]/20 data-[placeholder]:text-zinc-400"
                    data-testid="lead-loantype-trigger"
                  >
                    <SelectValue placeholder="Select loan type" />
                  </SelectTrigger>
                  <SelectContent data-testid="lead-loantype-content">
                    {LOAN_TYPES.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`loantype-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelCls} htmlFor="lead-income">Monthly Income</label>
                <input
                  id="lead-income"
                  name="monthly_income"
                  type="text"
                  placeholder="e.g. ₹50,000"
                  className={inputCls}
                  data-testid="lead-income-input"
                />
              </div>
            </div>

            {status === "error" && (
              <p className="text-sm text-red-600" data-testid="lead-error-message">
                {errorMsg}
              </p>
            )}

            <button
              type="submit"
              disabled={status === "sending"}
              className="group relative inline-flex w-full items-center justify-center gap-2 rounded-full brand-gradient px-8 py-4 font-medium text-white transition-transform hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(108,43,217,0.3)] disabled:opacity-70"
              data-testid="lead-submit-button"
            >
              {status === "sending" ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" /> Sending...
                </>
              ) : (
                <>
                  Get Callback <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>
            <p className="text-center text-xs text-zinc-400">
              By submitting you agree to be contacted by CARDED. We respect your privacy.
            </p>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LeadForm;
